/**
 * Example components demonstrating the real-time WebSocket system.
 *
 * These examples show how the real-time updates work in Good Outcomes,
 * including signal processing progress, organization updates, and manual subscriptions.
 */

import React from 'react';
import {
  useRealtimeConnection,
  useRealtimeSubscriptions,
  useOrganizationUpdates
} from '../../hooks/useRealtimeUpdates';
import { RealtimeMessage } from '../../services/RealtimeClient';
import { Timestamp } from '../ui/timestamp';

/**
 * Connection status indicator - shows WebSocket connection health
 */
export const RealtimeConnectionStatus: React.FC = () => {
  const { connectionState, stats, isConnected, reconnect } = useRealtimeConnection();

  return (
    <div className="flex items-center gap-2 text-sm">
      <div
        className={`w-2 h-2 rounded-full animate-pulse ${
          isConnected ? 'bg-green-500' : 'bg-red-500'
        }`}
        title={`WebSocket: ${connectionState}`}
      />
      <span className="font-medium">
        {isConnected ? 'Connected' : connectionState}
      </span>
      {stats.subscriptionCount > 0 && (
        <span className="text-gray-500">
          ({stats.subscriptionCount} subscription{stats.subscriptionCount !== 1 ? 's' : ''})
        </span>
      )}
      {stats.messagesReceived > 0 && (
        <span className="text-gray-400">
          {stats.messagesReceived} messages
        </span>
      )}
      {!isConnected && (
        <button
          type="button"
          onClick={reconnect}
          className="text-blue-600 hover:underline text-xs"
        >
          Reconnect
        </button>
      )}
    </div>
  );
};

/**
 * Signal processing progress display - shows real-time reclassification progress
 */
export const SignalProgressDemo: React.FC<{ signalId: string }> = ({ signalId }) => {
  const { subscribe } = useRealtimeSubscriptions();
  const [progress, setProgress] = React.useState<{
    percent: number;
    stage: string;
    timestamp: number;
  } | null>(null);
  const [isComplete, setIsComplete] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = subscribe('signal', signalId, (message: RealtimeMessage) => {
      console.log(`[Signal ${signalId}] Received:`, message.type, message.data);

      if (message.type === 'signal.progress') {
        setProgress({
          percent: message.data?.percent || 0,
          stage: message.data?.stage || 'processing',
          timestamp: Date.now()
        });
        setIsComplete(false);
      } else if (message.type === 'signal.classified') {
        setProgress(null);
        setIsComplete(true);
        // Auto-hide after 3 seconds
        setTimeout(() => setIsComplete(false), 3000);
      }
    });

    return unsubscribe;
  }, [signalId, subscribe]);

  if (!progress && !isComplete) {
    return null;
  }

  const getTimeDisplay = () => {
    if (!progress?.timestamp) return '';
    const elapsed = Math.floor((Date.now() - progress.timestamp) / 1000);
    if (elapsed < 5) return 'just now';
    if (elapsed < 60) return `${elapsed}sec`;
    return `${Math.floor(elapsed / 60)}min`;
  };

  const getStageDisplay = (stage: string) => {
    const stages: Record<string, string> = {
      starting: 'Starting',
      analyzing: 'Analyzing',
      classifying: 'Classifying',
      processing: 'Processing',
      saving_results: 'Saving',
      summarizing: 'Summarizing',
      evaluating_ombudsman: 'Evaluating FOS',
      done: 'Completed'
    };
    return stages[stage] || stage;
  };

  if (isComplete) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full text-sm animate-pulse-success">
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="font-medium text-green-700">Classification Complete!</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm">
      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
      <span className="font-medium">
        {getStageDisplay(progress?.stage || 'processing')}
      </span>
      <span className="text-gray-600">
        {progress?.percent || 0}%
      </span>
      <span className="text-gray-500 text-xs">
        {getTimeDisplay()}
      </span>
    </div>
  );
};

/**
 * Organization activity monitor - shows all real-time messages for an org
 */
export const OrganizationActivityMonitor: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const { messages } = useOrganizationUpdates(organizationId);
  const [filter, setFilter] = React.useState<string>('all');

  // Filter messages
  const filteredMessages = React.useMemo(() => {
    if (filter === 'all') return messages.slice(-10).reverse();
    return messages
      .filter(m => m.type?.includes(filter))
      .slice(-10)
      .reverse();
  }, [messages, filter]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-800">Organization Activity</h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-sm border rounded px-2 py-1"
        >
          <option value="all">All Messages</option>
          <option value="signal.progress">Progress</option>
          <option value="signal.classified">Classified</option>
          <option value="embedding">Embeddings</option>
        </select>
      </div>

      <div className="space-y-1 max-h-64 overflow-y-auto">
        {filteredMessages.length === 0 ? (
          <p className="text-gray-500 text-sm py-2">No messages yet...</p>
        ) : (
          filteredMessages.map((message, index) => (
            <div
              key={index}
              className="text-xs p-2 bg-gray-50 rounded border hover:bg-gray-100 transition-colors"
            >
              <div className="flex justify-between items-start">
                <span className={`font-medium ${
                  message.type?.includes('progress') ? 'text-blue-600' :
                  message.type?.includes('classified') ? 'text-green-600' :
                  'text-gray-700'
                }`}>
                  {message.type}
                </span>
                <span className="text-gray-400">
                  <Timestamp value={new Date(message.timestamp || Date.now())} format="absolute" />
                </span>
              </div>
              {message.signal_id && (
                <p className="text-gray-600 mt-1">
                  Signal: ...{message.signal_id.slice(-8)}
                </p>
              )}
              {message.data?.stage && (
                <p className="text-gray-600">
                  Stage: {message.data.stage} ({message.data.percent}%)
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Manual subscription manager - demonstrates direct subscription API
 */
export const ManualSubscriptionManager: React.FC = () => {
  const { subscribe } = useRealtimeSubscriptions();
  const [subscriptions, setSubscriptions] = React.useState<Array<{
    id: string;
    channel: string;
    messageCount: number;
    lastMessage?: string;
    unsubscribe: () => void;
  }>>([]);
  const [newChannelType, setNewChannelType] = React.useState('signal');
  const [newChannelId, setNewChannelId] = React.useState('');

  const addSubscription = () => {
    if (!newChannelId) return;

    const channel = `${newChannelType}:${newChannelId}`;

    // Check if already subscribed
    if (subscriptions.some(s => s.channel === channel)) {
      alert('Already subscribed to this channel');
      return;
    }

    const sub = {
      id: Math.random().toString(36).substr(2, 9),
      channel,
      messageCount: 0,
      lastMessage: undefined as string | undefined,
      unsubscribe: () => {}
    };

    sub.unsubscribe = subscribe(newChannelType, newChannelId, (message: RealtimeMessage) => {
      console.log(`[${channel}] Message:`, message);
      setSubscriptions(prev => prev.map(s =>
        s.id === sub.id
          ? { ...s, messageCount: s.messageCount + 1, lastMessage: message.type }
          : s
      ));
    });

    setSubscriptions(prev => [...prev, sub]);
    setNewChannelId('');
  };

  const removeSubscription = (id: string) => {
    const sub = subscriptions.find(s => s.id === id);
    if (sub) {
      sub.unsubscribe();
      setSubscriptions(prev => prev.filter(s => s.id !== id));
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-800">Manual Subscriptions</h3>

      <div className="flex gap-2">
        <select
          value={newChannelType}
          onChange={(e) => setNewChannelType(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="signal">Signal</option>
          <option value="org">Organization</option>
          <option value="user">User</option>
        </select>
        <input
          type="text"
          value={newChannelId}
          onChange={(e) => setNewChannelId(e.target.value)}
          placeholder="Enter ID (e.g., UUID)"
          className="flex-1 border rounded px-2 py-1 text-sm"
        />
        <button
          onClick={addSubscription}
          disabled={!newChannelId}
          className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50"
        >
          Subscribe
        </button>
      </div>

      <div className="space-y-1">
        {subscriptions.length === 0 ? (
          <p className="text-gray-500 text-sm py-2">No active subscriptions</p>
        ) : (
          subscriptions.map((sub) => (
            <div key={sub.id} className="flex justify-between items-center p-2 bg-gray-100 rounded">
              <div className="flex-1">
                <span className="text-sm font-medium">{sub.channel}</span>
                <div className="text-xs text-gray-600">
                  {sub.messageCount} messages
                  {sub.lastMessage && ` • Last: ${sub.lastMessage}`}
                </div>
              </div>
              <button
                onClick={() => removeSubscription(sub.id)}
                className="text-red-600 hover:text-red-800 text-sm ml-2"
              >
                Unsubscribe
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Test signal trigger - simulates reclassification for testing
 */
export const TestSignalTrigger: React.FC = () => {
  const [signalId, setSignalId] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);

  const triggerReclassification = async () => {
    if (!signalId) return;

    setIsProcessing(true);
    try {
      // This would normally call your API endpoint
      console.log('Would trigger reclassification for:', signalId);
      alert(`Reclassification triggered for signal: ${signalId}\nWatch the progress indicator!`);
    } catch (error) {
      console.error('Failed to trigger reclassification:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-800">Test Signal Reclassification</h3>
      <div className="flex gap-2">
        <input
          type="text"
          value={signalId}
          onChange={(e) => setSignalId(e.target.value)}
          placeholder="Enter signal ID"
          className="flex-1 border rounded px-2 py-1 text-sm"
          disabled={isProcessing}
        />
        <button
          onClick={triggerReclassification}
          disabled={!signalId || isProcessing}
          className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600 disabled:opacity-50"
        >
          {isProcessing ? 'Processing...' : 'Reclassify'}
        </button>
      </div>
      {signalId && <SignalProgressDemo signalId={signalId} />}
    </div>
  );
};

/**
 * Main dashboard showing all real-time examples
 */
export const RealtimeExamplesDashboard: React.FC = () => {
  const orgId = React.useMemo(() => {
    // In production, get from user context
    return localStorage.getItem('organizationId') || 'demo-org-123';
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Real-time System Examples</h1>
          <p className="text-sm text-gray-600 mt-1">
            WebSocket demonstrations for the Good Outcomes platform
          </p>
        </div>
        <RealtimeConnectionStatus />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Signal */}
        <div className="p-4 border rounded-lg bg-white">
          <TestSignalTrigger />
        </div>

        {/* Organization Monitor */}
        <div className="p-4 border rounded-lg bg-white">
          <OrganizationActivityMonitor organizationId={orgId} />
        </div>

        {/* Manual Subscriptions */}
        <div className="p-4 border rounded-lg bg-white lg:col-span-2">
          <ManualSubscriptionManager />
        </div>
      </div>

      {/* System Info */}
      <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-3">
          🚀 Real-time System Architecture
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium text-blue-800 mb-1">Backend Flow</h4>
            <ul className="text-blue-700 space-y-1">
              <li>• Workers publish to Redis Pub/Sub channels</li>
              <li>• WebSocket manager routes to connections</li>
              <li>• Automatic subscription management</li>
              <li>• Progress tracking at each stage</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-purple-800 mb-1">Frontend Features</h4>
            <ul className="text-purple-700 space-y-1">
              <li>• Auto-reconnect with exponential backoff</li>
              <li>• Type-safe message handling</li>
              <li>• Automatic cleanup on unmount</li>
              <li>• Visual feedback with animations</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Channel Documentation */}
      <div className="p-4 bg-gray-50 border rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-2">📡 Channel Structure</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <code className="text-blue-600">org:{'{orgId}'}</code>
            <p className="text-gray-600 text-xs mt-1">Organization-wide updates</p>
          </div>
          <div>
            <code className="text-green-600">signal:{'{signalId}'}</code>
            <p className="text-gray-600 text-xs mt-1">Signal-specific progress</p>
          </div>
          <div>
            <code className="text-purple-600">user:{'{userId}'}</code>
            <p className="text-gray-600 text-xs mt-1">User notifications</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtimeExamplesDashboard;